import React, { useCallback, useEffect, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

/**
 * `window.confirm` con la cara del sistema.
 *
 * El nativo se traga en las tablets del club (memoria `confirm-se-traga-en-tablets`):
 * el operario pulsa «Salir», «Vaciar» o «Eliminar» y no pasa nada, sin decir por qué.
 * Y cuando sí aparece, no dice sobre qué actúa ni qué se lleva por delante.
 *
 * Este hook deja la llamada donde estaba —`if (!(await confirmar({...}))) return;`—
 * y pinta el `ConfirmDialog` de Pablo (`Club_Alert dialog`) en vez del cuadro del
 * navegador. Quien lo usa renderiza `dialogo` una vez, donde le venga bien.
 *
 *   const { confirmar, dialogo } = useConfirmar();
 *   ...
 *   if (!(await confirmar({ title: 'Vaciar el carrito', confirmLabel: 'Vaciar', tone: 'danger' }))) return;
 *   ...
 *   return (<>...{dialogo}</>);
 *
 * Una sola pregunta en vuelo: si llega otra mientras la primera está abierta, la
 * primera se resuelve a `false` (igual que si el operario hubiera cancelado). Y si el
 * componente se desmonta con la pregunta abierta, también `false`: nadie ejecuta un
 * borrado sobre una pantalla que ya no existe.
 */
export default function useConfirmar() {
  const [pendiente, setPendiente] = useState(null);
  const pendienteRef = useRef(null);

  const resolver = useCallback((valor) => {
    const actual = pendienteRef.current;
    pendienteRef.current = null;
    setPendiente(null);
    actual?.resolve(valor);
  }, []);

  const confirmar = useCallback((opciones) => new Promise((resolve) => {
    pendienteRef.current?.resolve(false);
    const nueva = { opciones: opciones || {}, resolve };
    pendienteRef.current = nueva;
    setPendiente(nueva);
  }), []);

  useEffect(() => () => {
    pendienteRef.current?.resolve(false);
    pendienteRef.current = null;
  }, []);

  const dialogo = (
    <ConfirmDialog
      active={pendiente != null}
      title={pendiente?.opciones.title || '¿Seguro?'}
      description={pendiente?.opciones.description ?? null}
      note={pendiente?.opciones.note ?? null}
      confirmLabel={pendiente?.opciones.confirmLabel || 'Aceptar'}
      cancelLabel={pendiente?.opciones.cancelLabel || 'Cancelar'}
      tone={pendiente?.opciones.tone || 'default'}
      onConfirm={() => resolver(true)}
      onCancel={() => resolver(false)}
    />
  );

  return { confirmar, dialogo };
}
